'use client';

import { forwardRef, useState, type FocusEvent } from 'react';

import { UzInput, type UzInputProps } from './UzInput';

/**
 * Number-only input. Restricts keystrokes to digits, optional decimals,
 * and minus sign. Emits numeric value via onValueChange in addition to
 * the standard onChange.
 *
 * While focused, the field shows the user's raw draft string instead of the
 * normalised controlled value. Without this, typing "12." in a controlled
 * field emits 12, the parent re-renders value=12, and the trailing decimal
 * point is destroyed - decimals become impossible to type.
 */
export interface UzNumberInputProps extends Omit<
  UzInputProps,
  'type' | 'onChange' | 'value' | 'defaultValue'
> {
  value?: number | '';
  defaultValue?: number | '';
  /** Number of decimal places allowed. 0 = integer only. */
  precision?: number;
  /** Allow negative values. */
  allowNegative?: boolean;
  min?: number;
  max?: number;
  step?: number;
  onValueChange?: (value: number | null) => void;
}

export const UzNumberInput = forwardRef<HTMLInputElement, UzNumberInputProps>(
  (
    {
      value,
      defaultValue,
      precision = 2,
      allowNegative = false,
      min,
      max,
      step: _step,
      onValueChange,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const decimalsRegex = precision > 0 ? `(?:\\.\\d{0,${precision}})?` : '';
    const pattern = `^${allowNegative ? '-?' : ''}\\d*${decimalsRegex}$`;

    /** Raw text while the user is typing; null = not editing (show prop). */
    const [draft, setDraft] = useState<string | null>(null);

    // Treat the input as uncontrolled when no value/defaultValue is provided.
    // Passing `value=""` would freeze the input because we don't manage state
    // here - we only emit changes via onValueChange.
    const controlledValue =
      draft !== null ? draft : value === undefined ? undefined : value === '' ? '' : String(value);
    const uncontrolledDefault =
      defaultValue === undefined ? undefined : defaultValue === '' ? '' : String(defaultValue);

    const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
      if (value !== undefined) {
        setDraft(value === '' ? '' : String(value));
      }
      onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
      // Commit from the DOM value (not React `draft` state) so a fast
      // Save click after the last keystroke never loses digits to a
      // stale closure. Empty / lone "-" is discarded so the controlled
      // `value` prop (last good number) shows again.
      const raw = e.target.value;
      if (raw !== '' && raw !== '-') {
        const num = Number(raw);
        if (
          !Number.isNaN(num) &&
          !(typeof min === 'number' && num < min) &&
          !(typeof max === 'number' && num > max)
        ) {
          onValueChange?.(num);
        }
      }
      setDraft(null);
      onBlur?.(e);
    };

    return (
      <UzInput
        ref={ref}
        type="text"
        inputMode={precision > 0 ? 'decimal' : 'numeric'}
        value={controlledValue}
        defaultValue={uncontrolledDefault}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '' || raw === '-') {
            setDraft(raw);
            onValueChange?.(null);
            return;
          }
          if (!new RegExp(pattern).test(raw)) return;
          const num = Number(raw);
          if (Number.isNaN(num)) return;
          if (typeof min === 'number' && num < min) return;
          if (typeof max === 'number' && num > max) return;
          setDraft(raw);
          onValueChange?.(num);
        }}
        {...rest}
      />
    );
  },
);
UzNumberInput.displayName = 'UzNumberInput';
